import styled from "@emotion/styled";
import { Tooltip } from "@mui/material";
import classNames from "classnames";
import type { HTMLAttributes, ReactNode } from "react";
import { useCallback, useRef, useState, type MouseEvent } from "react";

import { SkiliticsIcon } from "../icons";
import { DEFAULT_INPUT_HEIGHT, ellipsise } from "../utilities";
import { PropertiesPanelPopover } from "./PropertiesPanelPopover";

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
`;

const Label = styled.div`
  border-bottom: ${({ theme }) => `1px solid ${theme.surfaceSoft}`};
  padding: 7px 0;
  height: ${DEFAULT_INPUT_HEIGHT}px;
  width: 100%;
  transition: all 0.2s ease-in-out;
  min-width: 0; // css hack to allow flex child to ellipsise.
  user-select: none;
  ${ellipsise};
`;

const PopoverControlWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;

  margin-bottom: 5px;

  cursor: pointer;
  overflow: hidden;

  &.renderedContent {
    cursor: default;
    justify-content: flex-start;
    gap: 0;
    min-width: fit-content;

    margin: 0;
  }

  &:hover,
  &:active,
  &.open {
    color: ${({ theme }) => theme.primary};
    ${Label} {
      background: ${({ theme }) => theme.surface};
      border-color: ${({ theme }) => theme.outline};
    }
  }
`;

export type RenderContentProps = (props: { open: (anchor: HTMLElement) => void; close: () => void }) => ReactNode;

type Props = HTMLAttributes<HTMLDivElement> & {
  id: string;
  label?: ReactNode;
  icon?: ReactNode;
  popoverHeader?: ReactNode;
  tooltip?: ReactNode;
  panel?: "properties" | "context";
  renderContent?: RenderContentProps;
  /** Open the popover as soon as the control mounts (e.g. to keep it open after a remount). */
  defaultOpen?: boolean;
  children?: ReactNode;
};

export const PropertiesPanelPopoverControl = ({
  id,
  children,
  label = null,
  popoverHeader,
  tooltip,
  icon,
  panel = "properties",
  renderContent,
  defaultOpen = false,
  ...divProps
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Anchor to the wrapper once it mounts when `defaultOpen`; a stable callback ref fires on
  // mount only, so this won't reopen the popover after the user dismisses it.
  const openOnMount = useRef(defaultOpen);
  const wrapperRef = useCallback((element: HTMLDivElement | null) => {
    if (element && openOnMount.current) {
      openOnMount.current = false;
      setAnchorEl(element);
    }
  }, []);

  const handleOpen = (anchor: HTMLElement) => setAnchorEl(anchor);
  const handleClose = () => setAnchorEl(null);

  const handlePopoverClick = (event: MouseEvent<HTMLDivElement>) => !renderContent && handleOpen(event.currentTarget);

  const popoverOpen = Boolean(anchorEl);

  const content = label ? (
    <>
      <SkiliticsIcon icon="advanced-settings" size={16} />
      <Tooltip title={tooltip ?? label} placement="top">
        <Label>{label}</Label>
      </Tooltip>
    </>
  ) : (
    <Tooltip title={tooltip ?? popoverHeader} placement="top">
      <IconContainer>{icon ?? <SkiliticsIcon icon="advanced-settings" size={16} />}</IconContainer>
    </Tooltip>
  );

  return (
    <>
      <PopoverControlWrapper
        ref={wrapperRef}
        onClick={handlePopoverClick}
        className={classNames({ renderedContent: Boolean(renderContent), open: popoverOpen })}
        data-test={`popover-control-${id}`}
        {...divProps}
      >
        {renderContent?.({ open: handleOpen, close: handleClose }) ?? content}
      </PopoverControlWrapper>

      <PropertiesPanelPopover
        id={id}
        anchorEl={anchorEl}
        onClose={handleClose}
        header={popoverHeader ?? label}
        panel={panel}
      >
        {children}
      </PropertiesPanelPopover>
    </>
  );
};
